/**
 * Desktop-only sizing for the YouTube playlist picker dialog.
 *
 * Stored in localStorage as a UI preference. Never written to Firestore.
 */

export const PLAYLIST_PICKER_SIZE_KEY = 'rhythme.playlistPicker.size';
export const PLAYLIST_PICKER_MIN_WIDTH = 420;
export const PLAYLIST_PICKER_MIN_HEIGHT = 360;
export const PLAYLIST_PICKER_VIEWPORT_INSET = 32;
export const PLAYLIST_PICKER_DESKTOP_MIN_WIDTH = 768;

function finiteNumber(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function playlistPickerViewport(win = typeof window !== 'undefined' ? window : null) {
  return {
    width: finiteNumber(win?.innerWidth, 1280),
    height: finiteNumber(win?.innerHeight, 800),
  };
}

export function playlistPickerMaxSize(viewport = playlistPickerViewport()) {
  const width = Math.max(
    PLAYLIST_PICKER_MIN_WIDTH,
    finiteNumber(viewport.width, 1280) - PLAYLIST_PICKER_VIEWPORT_INSET,
  );
  const height = Math.max(
    PLAYLIST_PICKER_MIN_HEIGHT,
    finiteNumber(viewport.height, 800) - PLAYLIST_PICKER_VIEWPORT_INSET,
  );
  return { width, height };
}

export function canManuallyResizePlaylistPicker({
  pointerFine = null,
  viewportWidth = playlistPickerViewport().width,
  matchMedia = typeof window !== 'undefined' ? window.matchMedia?.bind(window) : null,
} = {}) {
  if (finiteNumber(viewportWidth, 0) < PLAYLIST_PICKER_DESKTOP_MIN_WIDTH) return false;

  if (typeof pointerFine === 'boolean') return pointerFine;

  if (typeof matchMedia === 'function') {
    try {
      return matchMedia('(pointer: fine) and (min-width: 768px)').matches === true;
    } catch {
      return finiteNumber(viewportWidth, 0) >= PLAYLIST_PICKER_DESKTOP_MIN_WIDTH;
    }
  }

  return finiteNumber(viewportWidth, 0) >= PLAYLIST_PICKER_DESKTOP_MIN_WIDTH;
}

export function clampPlaylistPickerSize(size = {}, viewport = playlistPickerViewport()) {
  const max = playlistPickerMaxSize(viewport);
  const width = Math.min(
    max.width,
    Math.max(PLAYLIST_PICKER_MIN_WIDTH, finiteNumber(size.width, PLAYLIST_PICKER_MIN_WIDTH)),
  );
  const height = Math.min(
    max.height,
    Math.max(PLAYLIST_PICKER_MIN_HEIGHT, finiteNumber(size.height, PLAYLIST_PICKER_MIN_HEIGHT)),
  );
  return { width: Math.round(width), height: Math.round(height) };
}

export function nextPlaylistPickerSize(origin, pointer, viewport = playlistPickerViewport()) {
  return clampPlaylistPickerSize({
    width: origin.width + (pointer.x - origin.x),
    height: origin.height + (pointer.y - origin.y),
  }, viewport);
}

export function readStoredPlaylistPickerSize(viewport = playlistPickerViewport()) {
  try {
    const raw = localStorage.getItem(PLAYLIST_PICKER_SIZE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return clampPlaylistPickerSize(parsed, viewport);
  } catch {
    return null;
  }
}

export function writeStoredPlaylistPickerSize(size, viewport = playlistPickerViewport()) {
  const clamped = clampPlaylistPickerSize(size, viewport);
  try {
    localStorage.setItem(PLAYLIST_PICKER_SIZE_KEY, JSON.stringify(clamped));
  } catch {
    // Ignore quota / private-mode failures.
  }
  return clamped;
}
